import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sprout, Moon, Infinity as InfinityIcon, Plus, Minus } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { Reveal, MaskedLines } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { IMAGES, API, startCheckout } from "../lib/constants";
import { scrollToId } from "../lib/lenis";

const WHAT_CARDS = [
  {
    icon: Sprout,
    title: "Self-knowledge, not shortcuts",
    body: "A tool for consciously repeating the beliefs you're choosing to grow — rewiring your mind, build new patterns over time.",
  },
  {
    icon: Moon,
    title: "Your daily ritual",
    body: "Play it as you fall asleep, on your way to work, or while you meditate. Reserve a moment of your day to align your subconscious with your goals.",
  },
  {
    icon: InfinityIcon,
    title: "Yours, forever",
    body: "MP3 to play as many times as you like, on any device, no subscription.",
  },
];

const FAQS = [
  {
    q: "How do the subliminal affirmations actually work?",
    a: "Your affirmations play at a low volume beneath a high-frequency soundscape — soft, but still audible, just below your conscious focus. Played consistently, especially while you sleep or meditate, your subconscious absorbs the message even when your mind isn't actively listening. Over time, that steady repetition is how new beliefs begin to form.",
  },
  {
    q: "What format do I receive?",
    a: "MP3, delivered by email, ready to download.",
  },
  {
    q: "Does this replace therapy or medical care?",
    a: "No. This is a wellness tool, not a clinical treatment. Please seek professional support if you need it.",
  },
  {
    q: "What information do I need to provide?",
    a: "You will be asked to describe your intention, your preferred wording, and any details that may help me personalize your affirmations. Please do not include highly sensitive personal information.",
  },
  {
    q: "Is my information kept private?",
    a: "Yes. The information you provide is used only to create your personalized audio and will not be shared publicly or with third parties.",
  },
  {
    q: "Does the audio include my name?",
    a: "Yes. Your name is included in the opening decree to make the experience feel more personal and intentional. This helps create a stronger sense of connection between you, your chosen intention, and the energy you are inviting into your life.",
  },
  {
    q: "How often should I listen to the audio?",
    a: "You may listen once or twice a day, or whenever it fits comfortably into your routine. Keep the volume at a safe and comfortable level.",
  },
  {
    q: "Are there any situations when I should not listen to the audio?",
    a: "Yes. Do not listen while driving, operating machinery, or doing anything that requires your full attention.",
  },
];

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="px-3 pt-24 sm:px-5" data-testid="hero-section">
      <div className="relative mx-auto max-w-[1500px] overflow-hidden rounded-[28px]">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img
            src={IMAGES.homeHero}
            alt="A person relaxing and listening to affirmation audio through headphones in warm golden light"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-cream/85 via-cream/40 to-transparent" />

        <div className="relative flex min-h-[86vh] items-center">
          <div className="w-full max-w-3xl px-7 py-20 sm:px-14 lg:px-20">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-xs font-medium uppercase tracking-[0.28em] text-terracotta"
            >
              Affirmation audio · co-created with you
            </motion.p>

            <h1 className="mt-6 font-serif text-[2.7rem] font-medium leading-[1.02] text-ink sm:text-6xl lg:text-7xl">
              <MaskedLines
                delay={0.3}
                lines={["A manifestation audio,", "created just for you."]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 max-w-xl text-base font-light leading-relaxed text-ink-soft sm:text-lg"
            >
              Share what you're ready to manifest in your life right now, and we'll transform your
              intentions into a personalized affirmation audio, thoughtfully created to support your
              subconscious and align your inner world with the reality you're choosing to create.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-9"
            >
              <PillButton
                variant="terracotta"
                onClick={startCheckout}
                data-testid="hero-cta"
                className="text-base"
              >
                Create my audio — €14,90
                <ArrowRight size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
              </PillButton>

              <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-soft">
                <span>Written by you</span>
                <span className="uf-diamond opacity-80" />
                <span>Delivered in 48h</span>
                <span className="uf-diamond opacity-80" />
                <span>Yours forever</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatItIs = () => (
  <section id="what-it-is" className="scroll-mt-24 px-6 py-28 lg:px-12" data-testid="what-it-is-section">
    <div className="mx-auto max-w-7xl">
      <Reveal>
        <p className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta">What it is</p>
        <h2 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.06] text-ink sm:text-5xl">
          Listen to your personalized manifestation every day.
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg">
          What do you want to manifest right now? True, aligned love? The job you've been dreaming
          of? Financial abundance? Your project finally taking off? Share your desires, and we'll
          transform them into a powerful manifestation audio — made entirely around you.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {WHAT_CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.12}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="flex h-full flex-col rounded-[24px] border border-[rgba(43,38,32,0.08)] bg-cream2 p-10"
              data-testid={`what-card-${i}`}
            >
              <span className="mb-8 flex h-12 w-12 items-center justify-center rounded-full border border-terracotta/30 text-terracotta">
                <c.icon strokeWidth={1.4} size={22} />
              </span>
              <h3 className="font-serif text-2xl font-medium text-ink">{c.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-ink-soft">{c.body}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PricingBand = () => {
  const [loading, setLoading] = useState(false);
  const startCheckout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: "audio",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setLoading(false);
      alert("Sorry, we couldn't start checkout. Please try again.");
    }
  };
  return (
  <section id="pricing" className="scroll-mt-24 px-3 py-6 sm:px-5" data-testid="pricing-section">
    <div className="uf-grain relative mx-auto max-w-[1500px] overflow-hidden rounded-[28px] bg-ink px-7 py-24 sm:px-14 lg:px-20">
      <div className="relative mx-auto grid max-w-6xl gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta-soft">
            Launch price
          </p>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.05] text-cream sm:text-5xl lg:text-6xl">
            Your manifestation audio, for just
          </h2>
          <p className="mt-4 text-sm font-medium uppercase tracking-[0.18em] text-terracotta-soft">
            Special launch price — limited time only
          </p>
          <div className="mt-6 flex flex-wrap items-end gap-x-5 gap-y-2">
            <span className="font-serif text-2xl text-cream/50 line-through decoration-terracotta-soft/70 sm:text-3xl">
              Regularly €19,90
            </span>
            <span className="font-serif text-7xl font-semibold leading-none text-terracotta-soft sm:text-8xl">
              Now €14,90
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-[24px] border border-white/10 bg-white/5 p-8 backdrop-blur-sm sm:p-10">
            <ul className="space-y-5">
              {[
                "Personalized 60 minute audio, layered at a high-frequency tone",
                "100% chosen by you",
                "Delivered within 48h",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-cream/90">
                  <span className="mt-2 uf-diamond" />
                  <span className="text-base leading-relaxed sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <PillButton
              onClick={startCheckout}
              disabled={loading}
              variant="light"
              className="mt-10 w-full text-base disabled:opacity-70"
              data-testid="pricing-pay-button"
            >
              {loading ? "Redirecting…" : "Create my audio — €14,90"}
              <ArrowRight size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
            </PillButton>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

const FaqItem = ({ item, open, onToggle, index }) => (
  <div className="border-b border-[rgba(43,38,32,0.12)]" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-6 py-7 text-left"
      data-testid={`faq-toggle-${index}`}
    >
      <span className="font-serif text-xl font-medium text-ink sm:text-2xl">{item.q}</span>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(43,38,32,0.2)] text-ink">
        {open ? <Minus size={16} strokeWidth={1.6} /> : <Plus size={16} strokeWidth={1.6} />}
      </span>
    </button>
    <motion.div
      initial={false}
      animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden"
    >
      <p className="max-w-2xl pb-7 text-base leading-relaxed text-ink-soft sm:text-lg">{item.a}</p>
    </motion.div>
  </div>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="scroll-mt-24 px-6 py-28 lg:px-12" data-testid="faq-section">
      <div className="mx-auto max-w-4xl">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta">FAQ</p>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.06] text-ink sm:text-5xl">
            Questions, answered.
          </h2>
        </Reveal>
        <div className="mt-12">
          {FAQS.map((item, i) => (
            <FaqItem
              key={i}
              index={i}
              item={item}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const storedPaid = localStorage.getItem("uf_paid") === "true";
    const storedSubmitted = localStorage.getItem("uf_submitted") === "true";
    // If a buyer lands on home post-payment, or has paid but not yet submitted,
    // send them to the dedicated affirmations page.
    if (params.get("paid") === "true" || (storedPaid && !storedSubmitted)) {
      navigate("/create-my-audio" + (params.get("paid") === "true" ? "?paid=true" : ""));
    }
  }, [navigate]);

  // Handle in-page hash navigation coming from other routes
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => scrollToId(id), 300);
    }
  }, [location.hash]);

  return (
    <div className="bg-cream">
      <Navbar />
      <main>
        <HeroSection />
        <div className="mt-6">
          <Marquee />
        </div>
        <WhatItIs />
        <PricingBand />
        <Faq />
      </main>
      <Footer />
    </div>
  );
};

export default Home;
